import { Attachment, Translations, UUID } from '@/types/types';
import { ProductTypeCategory } from './ProductTypeCategoryApi';
import { ApiQueryKey } from '@/configs/ApiQueryKey';
import { Attribute } from '@/api/AttributeApi';
import { ProductModifier } from '@/api/ComponentApi';
import { AccessoryCategory } from './AccessoryCategoriesApi';
import EntityApi, { BaseEntity } from './EntityApi';

export enum PriceRuleType {
	FIXED = 'fixed',
	PERCENTAGE = 'percentage',
	PER_SQUARE_METER = 'perSquareMeter',
}

export enum PropertyOperator {
	LTE = 'lte',
	GTE = 'gte',
	EQ = 'eq',
}

export enum RelationType {
	ATTRIBUTE_VALUE = 'attributeValue',
	COMPONENT = 'component',
	PRODUCT_PROPERTY = 'productProperty',
}

export enum PriceRuleDimensionConditionProperty {
	HEIGHT = 'height',
	WIDTH = 'width',
}

export interface PriceRuleDimensionCondition {
	property: PriceRuleDimensionConditionProperty;
	shortName: string;
	valueFrom: number;
	valueTo: number;
}

export interface PriceRule {
	amount: number;
	attributeValueIds: UUID[];
	type: PriceRuleType;
	dimensionConditions?: PriceRuleDimensionCondition[];
}

export interface AttributeValue extends BaseEntity {
	description: string;
	displayName: string;
	image: Attachment;
	listOrder: number;
	translations: Translations<{
		name: string;
		description: string;
	}>;
	value: string;
}

export interface ComponentAttributeValues extends BaseEntity {
	productModifiers: ProductModifier[];
	priceRule: ComponentPriceRule;
	attributeValue: AttributeValue;
	relations: Relation[];
}

export interface ComponentPriceRule {
	amount: number;
	type: PriceRuleType;
	isActive?: boolean;
}

export interface ComponentAttribute extends BaseEntity {
	attribute: Attribute;
	relations: Relation[];
	componentAttributeValues: ComponentAttributeValues[];
	priceRule: ComponentPriceRule;
	productModifiers: ProductModifier[];
}

export interface Relation {
	objectId: string;
	present: boolean;
	property: string;
	propertyOperator: PropertyOperator;
	propertyValue: string;
	type: RelationType;
}

export interface Component extends BaseEntity {
	name: string;
	componentAttributes: ComponentAttribute[];
}

export enum DimensionType {
	AUTO = 'auto',
	MANUAL = 'manual',
}

export enum ProductTemplate {
	SIMPLE = 'simple',
	ADVANCED = 'advanced',
}

export enum ProductTypeStatus {
	ACTIVE = 'active',
	INACTIVE = 'inactive',
}

export interface DimensionsConfigPriceModifier {
	amount: number;
	from: number;
	interval: number;
	attributeValueIds?: UUID[];
}

export interface SingleDimensionConfig {
	type: DimensionType;
	formula: string;
	min: number;
	max: number;
	priceModifiers?: DimensionsConfigPriceModifier[];
}

export interface DimensionsConfig {
	name: string;
	shortName: string;
	width: SingleDimensionConfig;
	height: SingleDimensionConfig;
	isSurfaceDimension: boolean;
}

export interface Accessory extends BaseEntity {
	category: AccessoryCategory;
	image?: Attachment;
	name: string;
	price: number;
}

export interface ProductType extends BaseEntity {
	name: string;
	description: string;
	price: number;
	category: ProductTypeCategory;
	image: Attachment | null;
	translations: Translations<{
		name: string;
		description: string;
	}>;
	status: ProductTypeStatus;
	dimensionsConfig: DimensionsConfig[];
	productTypePriceRules: PriceRule[];
	components: Component[];
	accessories: Accessory[];
	template: ProductTemplate;
}

class ProductTypeApi extends EntityApi<ProductType> {
	constructor() {
		super(ApiQueryKey.productTypes);
	}
}

export default new ProductTypeApi();
